import { Button, Container, Stack, Text } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { z } from 'zod';

import type { InviteUserRequestRole } from '@/api/generated/models/inviteUserRequestRole';
import { useListOrganisations } from '@/api/generated/organisations/organisations';
import { useInviteUser } from '@/api/generated/users/users';
import { IconFigmaArrowLeft, IconFigmaArrowRight } from '@/assets/icons';
import {
  CardSection,
  MutationButton,
  PageHeading,
  SelectField,
  TextField,
  Toast
} from '@/components/common';

const schema = z.object({
  organisationId: z.string().min(1),
  email: z.string().email(),
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  role: z.string().min(1)
});

type FormValues = z.infer<typeof schema>;

export const AdminInviteUserPage = () => {
  const { t } = useTranslation();

  const { data: orgsData, isLoading: orgsLoading } = useListOrganisations();
  const orgOptions = (orgsData ?? []).map((org) => ({
    value: org.id ?? '',
    label: org.name ?? ''
  }));

  const form = useForm<FormValues>({
    validate: zodResolver(schema),
    initialValues: { organisationId: '', email: '', firstName: '', lastName: '', role: 'USER' }
  });

  const inviteMutation = useInviteUser({
    mutation: { meta: { skipGlobalErrorToast: true }, onSuccess: () => form.reset() }
  });

  const handleSubmit = (values: FormValues) => {
    inviteMutation.mutate({
      data: {
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        organisationId: values.organisationId,
        role: values.role as InviteUserRequestRole
      }
    });
  };

  return (
    <Container size="lg" px={0}>
      <Stack gap={24}>
        <PageHeading
          title={t('adminInvitations.inviteTitle')}
          subtitle={t('adminInvitations.inviteSubtitle')}
        />

        <div style={{ maxWidth: 520, width: '100%' }}>
          <CardSection title={t('adminInvitations.newInvitationCrumb')}>
            <form onSubmit={form.onSubmit(handleSubmit)} noValidate>
              <Stack gap={16} p={20}>
                <SelectField
                  label={t('adminInvitations.fieldOrgLabel')}
                  required
                  placeholder={t('adminInvitations.fieldOrgPlaceholder')}
                  helperText={t('adminInvitations.fieldOrgHelper')}
                  data={orgOptions}
                  disabled={orgsLoading}
                  allowDeselect={false}
                  {...form.getInputProps('organisationId')}
                />

                <TextField
                  label={t('adminInvitations.fieldEmailLabel')}
                  required
                  placeholder={t('adminInvitations.fieldEmailPlaceholder')}
                  helperText={t('adminInvitations.fieldEmailHelper')}
                  {...form.getInputProps('email')}
                />

                <TextField
                  label={t('users.invite.firstName')}
                  required
                  {...form.getInputProps('firstName')}
                />

                <TextField
                  label={t('users.invite.lastName')}
                  required
                  {...form.getInputProps('lastName')}
                />

                <SelectField
                  label={t('adminInvitations.fieldRoleLabel')}
                  required
                  helperText={t('adminInvitations.fieldRoleHelper')}
                  allowDeselect={false}
                  data={[
                    { value: 'USER', label: t('users.invite.roleUser') },
                    { value: 'ORGANISATION_ADMIN', label: t('users.invite.roleAdmin') }
                  ]}
                  {...form.getInputProps('role')}
                />

                {inviteMutation.isError && (
                  <Text fz={13} style={{ color: 'var(--color-status-danger)' }}>
                    {t('users.invite.sendError')}
                  </Text>
                )}

                <MutationButton
                  type="submit"
                  isPending={inviteMutation.isPending}
                  isPaused={inviteMutation.isPaused}
                  rightSection={<IconFigmaArrowRight size={16} />}
                  w={{ base: '100%', sm: 'auto' }}
                >
                  {t('adminInvitations.sendButton')}
                </MutationButton>
              </Stack>
            </form>
          </CardSection>
        </div>

        {inviteMutation.isSuccess && (
          <Stack gap={16} style={{ maxWidth: 520, width: '100%' }}>
            <Toast tone="success">{t('adminInvitations.successMessage')}</Toast>

            <Button
              component={Link}
              to="/admin/invitations"
              variant="outline"
              leftSection={<IconFigmaArrowLeft size={18} />}
              w={{ base: '100%', sm: 'auto' }}
            >
              {t('adminInvitations.backToInvitations')}
            </Button>
          </Stack>
        )}
      </Stack>
    </Container>
  );
};
